import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, type Me } from '../lib/api';

/**
 * Catches the Webex Control Hub OAuth redirect at /api/admin-auth/callback
 * (matches the Redirect URI registered with the Webex Integration). Reads
 * code+state from the query string, POSTs to /api/wxcc/admin-auth/finalize,
 * then returns the user to Config Mgr with success/error markers.
 *
 * The path /api/admin-auth/callback is a SPA route — it never hits the API
 * gateway. That keeps the spec's registered Redirect URI working while letting
 * the actual token exchange run server-side under the user's session JWT.
 */
export default function AdminAuthCallback({ me: _me }: { me: Me }) {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [state, setState] = useState<'exchanging' | 'ok' | 'err'>('exchanging');
  const [msg, setMsg] = useState<string>('Completing Webex Control Hub sign-in…');

  useEffect(() => {
    const code = params.get('code');
    const stateP = params.get('state');
    const errParam = params.get('error');
    if (errParam) {
      setState('err');
      setMsg(`Webex returned an error: ${errParam}`);
      const t = setTimeout(() => nav(`/configuration?admin_auth=error&reason=${encodeURIComponent(errParam)}`, { replace: true }), 1500);
      return () => clearTimeout(t);
    }
    if (!code || !stateP) {
      setState('err');
      setMsg('Webex did not return code+state. Try again from Configuration → Connect Webex Control Hub.');
      const t = setTimeout(() => nav('/configuration?admin_auth=error&reason=missing_code_or_state', { replace: true }), 2000);
      return () => clearTimeout(t);
    }
    let cancel = false;
    (async () => {
      try {
        await api.adminAuthFinalize(code, stateP);
        if (cancel) return;
        setState('ok');
        setMsg('Connected. Returning to Configuration Manager…');
        setTimeout(() => nav('/configuration?admin_auth=connected', { replace: true }), 800);
      } catch (e) {
        if (cancel) return;
        const m = e instanceof Error ? e.message : String(e);
        setState('err');
        setMsg(`Token exchange failed: ${m}`);
        setTimeout(() => nav(`/configuration?admin_auth=error&reason=${encodeURIComponent(m).slice(0, 200)}`, { replace: true }), 2500);
      }
    })();
    return () => { cancel = true; };
  }, [params, nav]);

  return (
    <div className="h-[60vh] grid place-items-center">
      <div className="card p-6 max-w-md w-full text-center space-y-2">
        <div className="text-sm uppercase tracking-wide text-tnh-accent">Webex Control Hub</div>
        <div className={
          state === 'ok' ? 'text-tnh-ok font-medium'
          : state === 'err' ? 'text-tnh-err font-medium'
          : 'text-gray-500'
        }>{msg}</div>
        {state === 'exchanging' && <div className="text-xs text-gray-400 animate-pulse">Exchanging authorization code…</div>}
      </div>
    </div>
  );
}
